import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Gift, Sparkles, Crown, Building2, Star, Percent, Clock, CheckCircle, X } from 'lucide-react';

export default function ScrollingLogo() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [glowIntensity, setGlowIntensity] = useState(0);
  const [pulseScale, setPulseScale] = useState(1);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const lastScrollY = useRef(0);
  const scrollTimeout = useRef(null);
  const pulseInterval = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollSpeed = Math.abs(currentScrollY - lastScrollY.current);
      
      // Calculate glow intensity based on scroll speed (0 to 1)
      const intensity = Math.min(scrollSpeed / 30, 1);
      setGlowIntensity(intensity);
      
      // Pulse effect - scale based on scroll
      setPulseScale(1 + (intensity * 0.15));
      
      lastScrollY.current = currentScrollY;
      
      // Clear existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      
      // Reset glow and pulse when scrolling stops
      scrollTimeout.current = setTimeout(() => {
        setGlowIntensity(0);
        setPulseScale(1);
      }, 300);
    };

    // Subtle ambient pulse when not scrolling
    pulseInterval.current = setInterval(() => {
      if (glowIntensity === 0) {
        setPulseScale(prev => prev === 1 ? 1.05 : 1);
      }
    }, 1500);

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
      if (pulseInterval.current) clearInterval(pulseInterval.current);
    };
  }, [glowIntensity]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    setShowOfferModal(true);
  };

  const handleOfferAction = () => {
    setShowOfferModal(false);
    if (!user) {
      navigate('/?login=true');
    } else if (user.role === 'client') {
      navigate('/dashboard/client');
    } else if (user.role === 'vendor') {
      navigate('/dashboard/vendor');
    } else {
      navigate('/');
    }
  };

  // Different offers based on user role
  const getOfferContent = () => {
    if (!user) {
      return {
        title: "🎁 Exclusive Member Offers Await!",
        subtitle: "Sign up or login to unlock special deals",
        offers: [
          { icon: Percent, text: "Up to 30% off on first booking", highlight: true },
          { icon: Gift, text: "Free venue consultation worth ₹5,000" },
          { icon: Crown, text: "Priority access to premium properties" },
          { icon: Star, text: "Exclusive member-only rates" }
        ],
        buttonText: "Login to Unlock Offers",
        buttonStyle: "bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy"
      };
    } else if (user.role === 'client') {
      return {
        title: "🌟 Special Client Offers",
        subtitle: `Welcome back, ${user.name || 'Valued Client'}!`,
        offers: [
          { icon: Percent, text: "25% OFF on bulk bookings (10+ rooms)", highlight: true },
          { icon: Building2, text: "Free upgrade to premium venues this month" },
          { icon: Gift, text: "Complimentary corporate gift hamper (₹10K+ bookings)" },
          { icon: Clock, text: "48-hour priority response guarantee" },
          { icon: CheckCircle, text: "Flexible cancellation on select properties" }
        ],
        buttonText: "View My Dashboard",
        buttonStyle: "bg-gradient-to-r from-navy to-navy-700 hover:from-navy-700 hover:to-navy text-white"
      };
    } else if (user.role === 'vendor') {
      return {
        title: "🚀 Vendor Growth Offers",
        subtitle: `Hello, ${user.name || 'Partner'}!`,
        offers: [
          { icon: Crown, text: "FREE Featured Listing for 7 days", highlight: true },
          { icon: Star, text: "50% off Premium Subscription (Limited Time)" },
          { icon: Sparkles, text: "Priority placement in search results" },
          { icon: Building2, text: "Professional photography package at ₹9,999" },
          { icon: CheckCircle, text: "Dedicated account manager support" }
        ],
        buttonText: "View Vendor Dashboard",
        buttonStyle: "bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-600 text-white"
      };
    }
    return {
      title: "Special Offers",
      subtitle: "Check out our latest deals",
      offers: [],
      buttonText: "Explore",
      buttonStyle: "bg-gold text-navy"
    };
  };

  const offerContent = getOfferContent();

  return (
    <>
      {/* Floating Logo with Pulse & Glow Effect */}
      <button 
        onClick={handleLogoClick}
        className="fixed bottom-6 left-6 z-50 group cursor-pointer focus:outline-none"
        title="Click for Special Offers!"
      >
        <div className="relative">
          {/* Outer Glow Ring - Animated */}
          <div 
            className="absolute inset-0 rounded-full transition-all duration-300"
            style={{ 
              boxShadow: `0 0 ${20 + glowIntensity * 40}px ${5 + glowIntensity * 15}px rgba(212, 175, 55, ${0.3 + glowIntensity * 0.5})`,
              transform: `scale(${1.2 + glowIntensity * 0.3})`
            }} 
          />
          
          {/* Middle Glow Layer */}
          <div 
            className="absolute inset-0 bg-gold/20 rounded-full blur-xl transition-all duration-300"
            style={{ 
              opacity: 0.4 + glowIntensity * 0.6,
              transform: `scale(${1.3 + glowIntensity * 0.2})`
            }} 
          />
          
          {/* Inner Pulse Glow */}
          <div 
            className="absolute inset-0 bg-gradient-to-r from-gold/30 to-yellow-400/30 rounded-full blur-lg transition-all duration-200"
            style={{ 
              opacity: 0.5 + glowIntensity * 0.5,
              transform: `scale(${pulseScale * 1.1})`
            }} 
          />
          
          {/* Logo Image with Pulse Scale */}
          <img 
            src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
            alt="My Corporate Hub - Click for Offers"
            className="relative z-10 h-14 w-14 object-contain drop-shadow-xl transition-all duration-300 group-hover:scale-110"
            style={{ 
              transform: `scale(${pulseScale})`,
              filter: `brightness(${1 + glowIntensity * 0.2}) drop-shadow(0 0 ${5 + glowIntensity * 10}px rgba(212, 175, 55, ${0.5 + glowIntensity * 0.5}))`
            }}
          />
          
          {/* Click Indicator Badge */}
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center animate-bounce shadow-lg">
            <Gift className="w-3 h-3 text-white" />
          </div>
        </div>
      </button>

      {/* Special Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="sm:max-w-md border-0 shadow-2xl overflow-hidden p-0">
          {/* Header with Gradient */}
          <div className="bg-gradient-to-r from-navy via-navy-700 to-navy p-6 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gold/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gold/10 rounded-full blur-2xl" />
            <DialogHeader className="relative z-10">
              <DialogTitle className="text-2xl font-bold text-center">
                {offerContent.title}
              </DialogTitle>
              <p className="text-center text-slate-300 mt-2">{offerContent.subtitle}</p>
            </DialogHeader>
          </div>
          
          {/* Offers List */}
          <div className="p-6 space-y-4">
            {offerContent.offers.map((offer, index) => (
              <div 
                key={index}
                className={`flex items-center gap-4 p-3 rounded-lg transition-all ${
                  offer.highlight 
                    ? 'bg-gradient-to-r from-gold/20 to-yellow-100 border border-gold/30' 
                    : 'bg-slate-50 hover:bg-slate-100'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  offer.highlight ? 'bg-gold text-navy' : 'bg-navy/10 text-navy'
                }`}>
                  <offer.icon className="w-5 h-5" />
                </div>
                <span className={`font-medium ${offer.highlight ? 'text-navy font-semibold' : 'text-slate-700'}`}>
                  {offer.text}
                </span>
                {offer.highlight && (
                  <span className="ml-auto text-xs bg-red-500 text-white px-2 py-1 rounded-full animate-pulse">
                    HOT
                  </span>
                )}
              </div>
            ))}
            
            {/* CTA Button */}
            <Button 
              onClick={handleOfferAction}
              className={`w-full mt-6 py-6 text-lg font-bold shadow-lg transition-all hover:scale-[1.02] ${offerContent.buttonStyle}`}
            >
              <Sparkles className="w-5 h-5 mr-2" />
              {offerContent.buttonText}
            </Button>
            
            {/* Terms */}
            <p className="text-center text-xs text-slate-400 mt-4">
              *Terms & conditions apply. Offers valid for limited time only.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
